import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Formik, type FormikHelpers } from 'formik'
import * as Yup from 'yup'

import {
  createFirstNameValidationSchema,
  createLastNameValidationSchema,
  FirstNameInput,
  LastNameInput,
} from '@dhl/inputs'
import { useSnackbar, Button, StackColumn, Card } from '@dhl-ui/components'

import { dataLayerPublish } from '@dhl/datalayer'
import { CatInBoxIllustration } from '@dhl-ui/illustrations'
import { Page } from '@dhl-ui/page'
import { useGetProfile } from '~/profile/model/profile/hooks.js'
import { StepDescription } from './view/components/StepDescription.js'
import { useUpdateUserName } from '~/profile/model/user/hooks.js'
import { PortalSubPage } from '~/components-page/index.js'
import { ScrollablePageContent } from '~/components/ScrollablePageContent.js'
import { FooterButtonsContainer } from '~/components/index.js'

type FormValues = {
  firstName: string
  lastName: string
}

export const RequireFullNamePage: FC = () => {
  const { t } = useTranslation()
  const { data: profile, isLoading } = useGetProfile()
  const { enqueueSnackbar } = useSnackbar()
  const { mutateAsync: updateUserName } = useUpdateUserName()

  const validationSchema: Yup.Schema<FormValues> = Yup.object({
    firstName: createFirstNameValidationSchema(t),
    lastName: createLastNameValidationSchema(t),
  })

  const initialValues = {
    firstName: profile?.firstName ?? '',
    lastName: profile?.lastName ?? '',
  }

  const handleSubmit = async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
    try {
      await updateUserName({
        data: values,
      })
    } catch (error) {
      dataLayerPublish('ERROR', { error, page: 'require-full-name' })
      enqueueSnackbar(t('common.pleaseTryAgainErrorMessage'), { severity: 'error' })
      helpers.setSubmitting(false)
    }
  }

  return (
    <PortalSubPage
      data-testid="profile-require-fullname-page"
      headerBackground="subtle"
      isLoading={isLoading}
      title={t('portal.profileSetup.requireFullName.pageTitle')}
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validateOnChange={false}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {() => (
          <Form noValidate>
            <Page.Content isFullView>
              <ScrollablePageContent>
                <StackColumn gap={3}>
                  <StepDescription
                    slotProps={{ illustration: { variant: CatInBoxIllustration } }}
                    title={t('portal.profileSetup.requireFullName.stepTitle')}
                    description={t('portal.profileSetup.requireFullName.stepDescription')}
                  />

                  <Card>
                    <Card.Header title={t('portal.common.fillInYourDetails')} />
                    <Card.Content>
                      <StackColumn gap={2}>
                        <FirstNameInput name="firstName" fullWidth required />
                        <LastNameInput name="lastName" fullWidth required />
                      </StackColumn>
                    </Card.Content>
                  </Card>
                </StackColumn>
              </ScrollablePageContent>

              <FooterButtonsContainer>
                <Button fullWidth size="large" type="submit" variant="contained">
                  {t('portal.profileSetup.wizard.nextButton')}
                </Button>
              </FooterButtonsContainer>
            </Page.Content>
          </Form>
        )}
      </Formik>
    </PortalSubPage>
  )
}
